import React, { useRef, useEffect, useState } from 'react';
import { CustomLoader } from '../utils/CustomLoader';

const HEIGHT_MSG_TYPE = 'iframe-print-resize';

interface PrintDocumentProps {
  templateString: string,
  scale?: number
}

const PrintDocument: React.FC<PrintDocumentProps> = ({ templateString, scale = 1 }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    // Inject a postMessage reporter so the iframe can send its dimensions
    // without needing allow-same-origin. Appended before </body> if present,
    // otherwise just appended to the raw string.
    const resizeScript = `<script>
(function(){
  function report(){
    // document.body.scrollWidth is unreliable here: the template pins
    // html to a fixed --paper-width (e.g. 794px for A4) via CSS, but per
    // spec the root element's scrollable width is at least the viewport
    // width, even when its own CSS width is smaller. Since this iframe's
    // viewport starts wider than the page (100% of its flex container,
    // before we resize it below), scrollWidth reports that wider viewport
    // instead of the true page width, so the iframe gets sized too wide
    // and the actual (narrower) content sits flush-left with blank space
    // on the right. Read the real target width straight from the CSS
    // variable instead, falling back to scrollWidth only when it isn't a
    // fixed px value (e.g. the template's own small-screen '100%' rule).
    var paperWidth = getComputedStyle(document.documentElement).getPropertyValue('--paper-width').trim();
    var width = paperWidth.endsWith('px') ? parseFloat(paperWidth) : document.body.scrollWidth;
    window.parent.postMessage({
      type:'${HEIGHT_MSG_TYPE}',
      width: width,
      height: document.body.scrollHeight
    },'*');
  }
  if(document.readyState==='complete'||document.readyState==='interactive'){report();}
  else{window.addEventListener('load',report);}
  new MutationObserver(report).observe(document.documentElement,{childList:true,subtree:true});
  setTimeout(report,200);
  // The template's @font-face/@import web font (e.g. Poppins) loads
  // asynchronously and swaps in after the initial layout — that swap
  // changes text/column widths but doesn't mutate the DOM, so the
  // MutationObserver above never sees it. Re-report once fonts are
  // actually ready so the iframe isn't sized to the pre-swap (narrower)
  // fallback-font measurement, which clips the right edge of the content.
  if(document.fonts && document.fonts.ready){document.fonts.ready.then(report);}
})();
</script>`;

    const modified = templateString.includes('</body>')
      ? templateString.replace('</body>', resizeScript + '</body>')
      : templateString + resizeScript;

    iframe.srcdoc = modified;
  }, [templateString]);

  useEffect(() => {
    setIsLoading(true);

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== HEIGHT_MSG_TYPE) return;
      const iframe = iframeRef.current;
      if (!iframe) return;
      // Size the iframe to its exact natural content box so it never needs an
      // internal scrollbar of its own — any overflow scrolling happens on the
      // outer wrapper (see render below), outside the iframe's bordered frame,
      // so it can't eat into one side of the invoice's margins.
      iframe.style.width = event.data.width + 'px';
      iframe.style.height = (event.data.height + 30) + 'px';
    };

    window.addEventListener('message', handleMessage);
    setIsLoading(false);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return (
    <div className="d-flex justify-content-center w-100" style={{ maxHeight: 1000, overflowY: 'auto' }}>
      {isLoading ?
        <CustomLoader /> :
        <iframe
          ref={iframeRef}
          className="d-block border rounded"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            transformOrigin: 'center top',
            transform: `scale(${scale})`,
          }}
          sandbox="allow-scripts"
          scrolling="no"
          title={'Print Document'.toTranslatedDisplay()}
        />
      }
    </div>
  );
};

export default PrintDocument;
